// Story 7.12 Iteration 2 (ADO-911), DR-1 — per-SKU product resolution for the PDP.
//
// The PDP at `/products/:sku` must be DEEP-LINKABLE (DR-1). Two paths, in order:
//   1. HANDOFF — a listing card navigating to the PDP passes the `ProductSummary`
//      it already rendered in router state; `seed()` adopts it with no fetch.
//   2. DEEP LINK / refresh — no handoff, so `load()` reads the product by SKU.
//      `notFound` only when the SERVER says there is no such product, so the page
//      can render an explicit empty state rather than a permanent spinner.
//
// The two paths MEET on the recommendation surfaces: a carousel tile and an
// upsell-modal card hand over a STUB, not a read-model row, so that handoff is
// `seed(stub, { partial: true })` and `load()` still runs to fill it in. See
// `ProductDetailEntry.partial`.
//
// Story 7.19 AC8 (ADO-1385): the resolve is `GET /catalogue/products/:sku`. It
// used to go through the Story 7.2 keyword search, taking the exact-SKU row out
// of the results — which was not reliable. `search_text` weights name A against
// sku C and the read is capped at `SEARCH_RESULT_LIMIT`, so every product whose
// NAME contained the term outranked the product whose SKU WAS the term, and past
// the cap the requested row was simply absent: a real product rendered "Product
// not found", and correctness depended on SKU format not colliding with product
// names. `fetchProductDetail` also separates an authoritative 404 from a failed
// read (`null` vs throw), which the search path could not do at all.
//
// Story 7.15 (ADO-1372) AC1/AC2: the PDP's Product Information values and its
// category breadcrumb live in a SECOND, independent slice — `detailBySku`. They
// deliberately do NOT hang off the `bySku` entry above, because `load()`
// early-returns on `loaded && !partial` and a LISTING-CARD handover produces
// exactly that state: `seed(fullRow)` → `partial: false` → `load()` no-ops. Detail
// fields hung off that entry would populate on a deep link and stay EMPTY on a
// card click — the commonest arrival path. Giving the detail its own phase, loaded
// per SKU on its own terms, fixes that without adding a third meaning to `partial`
// (whose one meaning is "a recommendation stub owes a real row").
//
// ONE network call per visit, not two, even though there are two slices — but the
// mechanism is ASYMMETRIC and worth stating precisely, because the previous wording
// here overclaimed it as order-independent (code review 2026-08-03).
//
// `load()` and `loadDetail()` read the SAME endpoint. `load()` stamps BOTH slices
// `loading` synchronously before it awaits, and commits both from the one response, so
// a `loadDetail()` that follows it stands down. The reverse is NOT true: `load()`'s
// guards consult only `bySku`, so `loadDetail()` first on a cold SKU would fetch twice.
//
// That is safe because the ONLY caller, `ProductDetail`'s effect, calls `load` first —
// and `productDetail_loadBeforeLoadDetail_issuesExactlyOneRequest` pins that order.
// Making it genuinely order-free needs a shared in-flight promise map, which is more
// machinery than one redundant GET in an order nothing uses justifies. If a second
// caller ever appears, build the map rather than relying on the ordering.
//
// Copy of the `useRecommendationsStore` shape (per-SKU cache, generation-stamp
// guard, lazy load) — the repo's established Zustand store posture (ADR-004).
//
// Enabler E7-C (ADO-2274), D-1: a product's row depends on the selected ordering
// entry's range, so both reads name the active entry, and the cache is reset when
// the entry changes (`startCatalogueEntryResets`); the reset's generation bump
// drops a fetch for the old entry that lands late.

import { create } from 'zustand';
import { fetchProductDetail, type ProductDetail, type ProductSummary } from '../api/browse.client';
import { resolveCatalogueEntryKey } from '../ordering/catalogueEntry';

export type ProductDetailPhase = 'loading' | 'loaded' | 'notFound' | 'error';

export interface ProductDetailEntry {
  readonly phase: ProductDetailPhase;
  /** The resolved product; `null` until loaded (and on notFound/error). */
  readonly product: ProductSummary | null;
  /**
   * `true` when `product` is a KNOWN-INCOMPLETE row — a `toProductSummary(...)`
   * stub handed over by a carousel tile or an upsell-modal card, which carries no
   * `description` and no `unitOfMeasure` (they are hard-set to null, never
   * invented). A partial entry renders immediately (it has a name, image and pack
   * size) but stays RE-RESOLVABLE: `load()` treats it as work still to do, and
   * replaces it with the full read-model row.
   *
   * Without this, a cold-cache visit from a rail or the modal — the COMMON path,
   * since a tile is usually the first time that SKU is seen — seeded the stub
   * straight to `loaded`, `load()` early-returned on `loaded` forever after, and
   * the hero's Pack/Case silently lost its unit of measure for the whole session.
   * The `seed()` degradation guard below only covers the other order (stub AFTER
   * a full row), so it could not catch this one.
   */
  readonly partial: boolean;
}

/**
 * Story 7.15 (ADO-1372) — the PDP-only half of a product: its Product Information
 * values and its category breadcrumb.
 *
 * A SEPARATE entry from {@link ProductDetailEntry} because it can only ever come
 * from the SERVER. A listing card can hand over a full `ProductSummary`, which is
 * why `load()` is allowed to skip its fetch — but no handover can carry these
 * fields, so they need a request of their own regardless of how the page was
 * reached. `detail === null` on `notFound`/`error`, and while a first load is in
 * flight; the panel simply renders nothing (AC3), never an error.
 */
export interface ProductDetailFieldsEntry {
  readonly phase: ProductDetailPhase;
  readonly detail: ProductDetail | null;
}

interface ProductDetailStoreState {
  /** Per-SKU cache. Absent = never requested. */
  readonly bySku: Readonly<Record<string, ProductDetailEntry>>;
  /** Story 7.15 (ADO-1372) — per-SKU PDP detail cache (Product Information +
   *  breadcrumb). Absent = never requested. Independent of `bySku` on purpose —
   *  see the file header. */
  readonly detailBySku: Readonly<Record<string, ProductDetailFieldsEntry>>;
  /**
   * Adopt a product handed over from a listing (router state) with no fetch.
   * Idempotent, and non-destructive: a handed row that is LESS complete than the
   * cached one (a recommendation-derived stub, which carries no `description` /
   * `unitOfMeasure`) is ignored rather than allowed to degrade a resolved entry.
   *
   * `partial` is declared by the HANDING SURFACE, not inferred here: a real
   * read-model row may legitimately have a null `description`, so guessing would
   * fire a pointless re-fetch on ordinary products. The recommendation surfaces
   * know they are handing a stub and say so.
   */
  seed: (product: ProductSummary, options?: { readonly partial?: boolean }) => void;
  /**
   * Resolve `sku` (idempotent). A no-op while a fetch for that SKU is in flight
   * or already FULLY resolved; a `partial` (stub-seeded) entry, and an `error`
   * entry, may re-resolve. A partial entry keeps its product visible while the
   * repair runs, so the page does not flash back to "Loading product…".
   *
   * Story 7.15 (ADO-1372): when it DOES fetch, it commits BOTH slices from the one
   * response — the endpoint returns the detail fields too, so making `loadDetail`
   * repeat the call would be a second identical GET per visit. When it no-ops (the
   * card-handover path) `loadDetail` is what does the work.
   */
  load: (sku: string) => Promise<void>;
  /**
   * Story 7.15 (ADO-1372) — resolve the PDP detail (Product Information +
   * breadcrumb) for `sku`. Idempotent, and UNCONDITIONAL with respect to how the
   * page was reached: unlike `load`, a handover can never satisfy it.
   *
   * A no-op while a request for that SKU is in flight (including one `load` started
   * for the same endpoint) or already resolved; an `error` entry may re-resolve.
   *
   * Call this AFTER `load` when calling both — see the file header. `load` claims the
   * shared request; this one cannot, so `loadDetail`-then-`load` on a cold SKU fetches
   * twice.
   */
  loadDetail: (sku: string) => Promise<void>;
  /** Test/session hygiene — drop all state. */
  reset: () => void;
}

/** Immutably set one SKU's entry in a per-SKU map. Shared by both slices. */
function withEntry<T>(
  bySku: Readonly<Record<string, T>>,
  sku: string,
  entry: T,
): Readonly<Record<string, T>> {
  return { ...bySku, [sku]: entry };
}

// Monotonic generation stamp: `reset()` bumps it and a `load()` only commits its
// result if the stamp is unchanged, so a fetch resolving after a reset (logout /
// test hygiene) cannot resurrect a stale entry. Mirrors the sibling stores.
let generation = 0;

export const useProductDetailStore = create<ProductDetailStoreState>((set, get) => ({
  bySku: {},
  detailBySku: {},

  seed(product, options) {
    // NOT every handed row is a full read-model row. A listing card hands over
    // the row it rendered, but a carousel tile and an upsell-modal card hand
    // over a `toProductSummary(...)` STUB synthesised from a `RecommendedProduct`
    // — which carries no `description` and no `unitOfMeasure` (they are hard-set
    // to null, never invented). Overwriting unconditionally meant one visit via
    // a rail or the modal permanently degraded an already-resolved product: the
    // entry stayed `loaded`, so `load()` would never repair it, and the hero's
    // Pack/Case silently lost its unit of measure for the rest of the session.
    // Keep the richer row: only seed when there is nothing loaded yet, or when
    // the incoming row is at least as complete as the cached one.
    const existing = get().bySku[product.sku];
    if (existing?.phase === 'loaded' && existing.product !== null) {
      const cached = existing.product;
      const losesDescription = cached.description !== null && product.description === null;
      const losesUnitOfMeasure = cached.unitOfMeasure !== null && product.unitOfMeasure === null;
      if (losesDescription || losesUnitOfMeasure) return;
    }
    set({
      bySku: withEntry(get().bySku, product.sku, {
        phase: 'loaded',
        product,
        partial: options?.partial === true,
      }),
    });
  },

  async load(sku) {
    const existing = get().bySku[sku];
    if (existing?.phase === 'loading') return;
    // A stub-seeded entry is `loaded` but still owes a real row, so it is NOT a
    // no-op — it is exactly the case a plain `loaded` check used to swallow.
    if (existing?.phase === 'loaded' && !existing.partial) return;
    // Enabler E7-C (ADO-2274), D-1: the product is read in the selected entry's range. With no
    // entry yet there is nothing to ask; the page re-invokes `load` once one is chosen.
    const entry = resolveCatalogueEntryKey();
    if (entry === null) return;

    const gen = generation;
    // Keep a partial row on screen while it is being repaired: blanking it would
    // replace a rendered hero with the loading placeholder mid-visit.
    //
    // Story 7.15 (ADO-1372): the detail slice is stamped `loading` in the SAME
    // synchronous `set`, BEFORE the await. `loadDetail` reads the same endpoint, so
    // this is what stops the two from both firing when the page calls them in one
    // effect — whoever gets here first claims the request, in either order.
    set({
      bySku: withEntry(get().bySku, sku, {
        phase: 'loading',
        product: existing?.product ?? null,
        partial: existing?.partial ?? false,
      }),
      detailBySku: withEntry(get().detailBySku, sku, {
        phase: 'loading',
        detail: get().detailBySku[sku]?.detail ?? null,
      }),
    });
    try {
      // An equality read on the SKU — no ranking, no cap, so the row is either
      // there for this tenant and account type or it is not (Story 7.19 AC8).
      const product = await fetchProductDetail(entry, sku);
      if (gen !== generation) return; // a reset() happened mid-fetch — drop the stale result
      if (product !== null) {
        // The real read-model row — nothing left to repair. One response, both
        // slices (Story 7.15): the summary half for the hero and the handover
        // consumers, the detail half for the Product Information panel + breadcrumb.
        set({
          bySku: withEntry(get().bySku, sku, { phase: 'loaded', product, partial: false }),
          detailBySku: withEntry(get().detailBySku, sku, { phase: 'loaded', detail: product }),
        });
        return;
      }
      // The server says this SKU has no product for this caller. On a COLD entry
      // that is the not-found state. On a REPAIR it is not: the stub came from a
      // tile inside this app, so keep the row that is already on screen rather
      // than replacing a rendering page with "Product not found" — the two can
      // now only disagree if the product left the caller's assortment between the
      // tile and the visit, and the rendered row is the better answer for that.
      const fallback = existing?.product ?? null;
      set({
        bySku: withEntry(get().bySku, sku, {
          phase: fallback === null ? 'notFound' : 'loaded',
          product: fallback,
          partial: false,
        }),
        // Story 7.15: the DETAIL is `notFound` either way. The row kept on screen
        // came from a tile inside this app, and a tile carries no detail fields, so
        // there is nothing to fall back to — the panel renders nothing and the
        // breadcrumb stays bare (AC3), rather than showing stale or invented values.
        detailBySku: withEntry(get().detailBySku, sku, { phase: 'notFound', detail: null }),
      });
    } catch {
      if (gen !== generation) return; // a reset() happened mid-fetch — drop the stale error
      // Same asymmetry on the failure path, and the entry stays `partial` so a
      // later visit can still repair it.
      const fallback = existing?.product ?? null;
      set({
        bySku: withEntry(get().bySku, sku, {
          phase: fallback === null ? 'error' : 'loaded',
          product: fallback,
          partial: fallback !== null,
        }),
        // `error`, not `loaded`-with-nothing: that is what makes `loadDetail`
        // re-resolvable on a later visit (a `loaded` detail entry is terminal).
        //
        // The already-resolved detail is PRESERVED, exactly as the loading stamp above
        // preserves it. A transient failure must not blank a correct panel: returning to
        // a previously-viewed SKU via a stub tile makes `load` re-resolve (`loaded &&
        // partial`), and nulling here collapsed Product Information from four rows to
        // `Code` alone and the trail from three crumbs to bare — for a product whose
        // detail was right moments earlier, with no repair left in that navigation.
        detailBySku: withEntry(get().detailBySku, sku, {
          phase: 'error',
          detail: get().detailBySku[sku]?.detail ?? null,
        }),
      });
    }
  },

  async loadDetail(sku) {
    const existing = get().detailBySku[sku];
    if (existing?.phase === 'loading') return;
    // `loaded` and `notFound` are both answers from the server — terminal. Only an
    // `error` (a failed read, which may be transient) is worth retrying, matching
    // how `load` treats its own entry.
    if (existing?.phase === 'loaded' || existing?.phase === 'notFound') return;
    const entry = resolveCatalogueEntryKey();
    if (entry === null) return;

    const gen = generation;
    set({
      detailBySku: withEntry(get().detailBySku, sku, {
        phase: 'loading',
        detail: existing?.detail ?? null,
      }),
    });
    try {
      const detail = await fetchProductDetail(entry, sku);
      if (gen !== generation) return; // a reset() happened mid-fetch — drop the stale result
      set({
        detailBySku: withEntry(get().detailBySku, sku, {
          phase: detail === null ? 'notFound' : 'loaded',
          detail,
        }),
      });
    } catch {
      if (gen !== generation) return; // a reset() happened mid-fetch — drop the stale error
      set({ detailBySku: withEntry(get().detailBySku, sku, { phase: 'error', detail: null }) });
    }
  },

  reset() {
    generation += 1;
    set({ bySku: {}, detailBySku: {} });
  },
}));
